import React, { useEffect, useState } from 'react';

import { GGJLogoMark } from '../components/Brand/GGJLogo';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileAppLoader from '../components/mobile/MobileAppLoader';
import { isNativePlatform } from '../utils/capacitorUtils';

import { UserProfileProvider, useUserProfile } from '../modules/dashboard/hooks/useUserProfile';

import { AUTH_TRANSITION_START_EVENT } from './router/postAuthLanding';

import AppProviders from './providers/AppProviders';
import AppRouter from './router/AppRouter';
import { useAppState } from './store/AppStateManager';
import { isPaymentBootRoute, isPaymentReturnPath } from '../utils/paymentReturnBootstrap';


/** Ensure theme class is on document so error/loading screens use correct theme (ThemeProvider is not mounted yet). */
function useThemeClass() {
  useEffect(() => {
    const saved = localStorage.getItem('app-theme');
    if (saved === 'light' || saved === 'dark') {
      document.documentElement.classList.remove('light', 'dark');
      document.documentElement.classList.add(saved);
      return;
    }
    const dark = typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    document.documentElement.classList.remove('light', 'dark');
    document.documentElement.classList.add(dark ? 'dark' : 'light');
  }, []);
}

function AppWithProfile() {
  useThemeClass();
  const { isLoading: profileLoading } = useUserProfile();
  const [authTransition, setAuthTransition] = useState(false);
  const {
    appState,
    handleNavigate,
    handleAuthSuccess,
    handleSignOut,
    session,
    user,
    consentState
  } = useAppState();

  useEffect(() => {
    const onStart = () => setAuthTransition(true);
    window.addEventListener(AUTH_TRANSITION_START_EVENT, onStart);
    return () => window.removeEventListener(AUTH_TRANSITION_START_EVENT, onStart);
  }, []);

  useEffect(() => {
    if (!authTransition) return;
    if (
      appState.currentView === 'agent' &&
      session &&
      !profileLoading &&
      !appState.loading
    ) {
      const id = requestAnimationFrame(() => setAuthTransition(false));
      return () => cancelAnimationFrame(id);
    }
  }, [authTransition, appState.currentView, appState.loading, session, profileLoading]);

  // Signal the prerender plugin (puppeteer) that the real routed view is
  // mounted — NOT the loading spinner. Without this the snapshot captures
  // <LoadingSpinner/> because requestIdleCallback fires before the Supabase
  // session check resolves. Dispatched once, after appState.loading turns
  // false, with a double-RAF to let AppRouter paint first.
  // See: vite.config.ts rendererOptions.renderAfterDocumentEvent.
  const prerenderFiredRef = React.useRef(false);
  useEffect(() => {
    if (prerenderFiredRef.current) return;
    if (appState.loading) return;
    if (typeof window === 'undefined') return;
    // Blog posts load async from Supabase — BlogPostPage dispatches when ready.
    const path =
      window.location.pathname.length > 1 && window.location.pathname.endsWith('/')
        ? window.location.pathname.slice(0, -1)
        : window.location.pathname;
    if (path.startsWith('/blog/') && path.length > '/blog/'.length) return;
    if (path.startsWith('/careers/') && path.length > '/careers/'.length && !path.endsWith('/apply')) return;
    prerenderFiredRef.current = true;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.dispatchEvent(new Event('ggj-prerender-ready'));
      });
    });
    return () => cancelAnimationFrame(raf1);
  }, [appState.loading]);

  // On fresh page load appState.loading starts true — show card immediately.
  // For in-session transitions (sign-in/sign-out without page reload) loading resolves
  // in < 400ms, so we delay to avoid a brief flash. We track whether we've already
  // seen loading=false at least once to distinguish the two cases.
  const seenIdleRef = React.useRef(false);
  const [showLoadingCard, setShowLoadingCard] = useState(appState.loading); // true on fresh load

  const paymentBootRoute = typeof window !== 'undefined'
    && (isPaymentBootRoute(window.location.pathname)
      || isPaymentReturnPath(window.location.pathname, window.location.search));

  useEffect(() => {
    if (!appState.loading) {
      seenIdleRef.current = true;
      setShowLoadingCard(false);
      return;
    }
    // Fresh page load: seenIdle is still false → show immediately
    if (!seenIdleRef.current) {
      setShowLoadingCard(true);
      return;
    }
    // In-session transition: wait 400ms before showing to suppress brief flashes
    const t = setTimeout(() => setShowLoadingCard(true), 400);
    return () => clearTimeout(t);
  }, [appState.loading]);

  if (appState.loading && showLoadingCard) {
    // Native: full-screen splash; Web: simple centered spinner (no branded card)
    if (isNativePlatform()) {
      return <MobileAppLoader phase="auth" />;
    }

    // Payment routes: prerendered homepage lives in index.html — show neutral spinner only.
    if (paymentBootRoute) {
      return (
        <div
          className="min-h-screen min-h-[100dvh] flex items-center justify-center vscode-bg-primary"
          role="status"
          aria-busy="true"
          aria-label="Loading payment"
        >
          <LoadingSpinner size="xl" showText={false} />
        </div>
      );
    }

    return (
      <div
        className="min-h-screen min-h-[100dvh] flex items-center justify-center vscode-bg-primary vscode-transition"
        role="status"
        aria-live="polite"
        aria-label="Loading application"
      >
        <LoadingSpinner size="xl" showText={false} />
      </div>
    );
  }

  // In-session loading but under the 400ms threshold — native shows loader, web shows nothing (no flash)
  if (appState.loading) {
    return isNativePlatform()
      ? <MobileAppLoader phase="auth" />
      : null;
  }

  // Show error screen if there's an initialization error – branded popup (same style as payment success)
  if (appState.error) {
    return (
      <div
        className="min-h-screen min-h-[100dvh] vscode-bg-primary flex items-center justify-center py-4 sm:py-6 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] px-3 sm:px-4 vscode-transition"
      >
        <div className="w-full max-w-md flex flex-col rounded-2xl vscode-card shadow-2xl border vscode-border overflow-hidden bg-[var(--vscode-bg-elevated)]">
          {/* Header: app logo + title (match PaymentSuccessPage / CreditPurchaseModal) */}
          <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-6 py-4 sm:py-5 border-b vscode-border bg-[var(--vscode-bg-secondary)] min-h-[56px]">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden">
              <GGJLogoMark size={44} />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white truncate">GoGrabJob</span>
              <span className="text-xs font-medium text-[var(--vscode-primary)] uppercase tracking-wide">Sign in required</span>
            </div>
          </div>
          <div className="p-4 sm:p-6 lg:p-8 text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-full bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center shrink-0">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-amber-500 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold vscode-text-primary mb-2">Authentication Required</h2>
            <p className="vscode-text-primary text-base leading-relaxed mb-1">
              {appState.error}
            </p>
            <p className="vscode-text-secondary text-sm mb-6">
              Sign in to continue or reload the app.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => { window.location.href = '/login'; }}
                className="flex-1 vscode-button-primary w-full px-6 py-3 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base min-h-[44px] touch-manipulation flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Sign In
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 vscode-bg-tertiary hover:vscode-bg-hover border vscode-border vscode-text-primary w-full px-6 py-3 sm:py-3.5 rounded-xl font-semibold text-sm sm:text-base min-h-[44px] touch-manipulation flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reload Application
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render app with router for all cases to ensure proper URL handling
  return (
    <AppProviders>
      <div className="ds-app">
        <AppRouter
          currentView={appState.currentView}
          onNavigate={handleNavigate}
          onAuthSuccess={handleAuthSuccess}
          onSignOut={handleSignOut}
          isAuthenticated={appState.isAuthenticated}
          session={session}
        />
        {authTransition && (
          <div
            className="fixed inset-0 z-[10001] flex items-center justify-center bg-[var(--vscode-bg-primary,#0f172a)]/92 backdrop-blur-sm"
            role="status"
            aria-live="polite"
            aria-label="Signing in"
          >
            <LoadingSpinner size="xl" showText={false} />
          </div>
        )}
      </div>
    </AppProviders>
  );
}

const App: React.FC = () => (
  <UserProfileProvider>
    <AppWithProfile />
  </UserProfileProvider>
);

export default App;
